/* eslint-disable react/prop-types */
import { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

import {
  selectLookupNames,
  selectAllComboBoxOptions,
  selectFormDefinition,
} from '@redux/selectors/FormSelector';

import {
  setFormDataInitialized,
} from '@redux/modules/formGeneration';

import { setRepeatableSectionCount } from '@redux/modules/listing';
import { selectPhotoMetaCollection } from '@redux/selectors/PhotoSelector';

const FormikFieldUpdater = ({
  agentGroupType,
  agentLookup,
  agentOfficeLookup,
  agentFormData,
  currCoords,
  officeLookup,
  officeManagerLookup,
  officeFormData,
  listingFormData,
  runValidation,
}) => {
  const formDefinition = useSelector(selectFormDefinition);
  const lookUpNames = useSelector(selectLookupNames);
  const allComboBoxOptions = useSelector(selectAllComboBoxOptions);
  const photoMetaCollection = useSelector(selectPhotoMetaCollection);
  const { values, setFieldValue } = useFormikContext();
  // const newSortOrder = useSelector(selectListingPhotoSortOrder);
  const dispatch = useDispatch();
  const generateComboBoxOptions = currComboOptionObject => (
    currComboOptionObject.data.flatMap(currOption => {
      if (!currOption.Active || !currOption.ParentIsActive) return [];

      return [{
        label: currOption.Description,
        value: `${currOption.Code ? currOption.Code : ''}`,
        parents: currOption.Parents,
      }];
    })
  );

  useEffect(() => {
    if (values?.Photos) {
      if (photoMetaCollection.length === 0 && Object.keys(values.Photos).length !== 0) values.Photos = {};
    }
    dispatch(setRepeatableSectionCount(values));
  }, [values.Rooms, values.Photos, values.Supplements, values.OpenHouses, values.Doors, values.Parcels, values.Units, values.History]);

  const updateFormikValues = updatedData => {
    if (!updatedData) return;
    const updatedWhiteList = Object.keys(updatedData);

    const formDefinitionFlatMap = formDefinition.flatMap(field => {
      if (!updatedWhiteList.includes(field.FieldName)) return [];
      return [{ ...field }];
    });

    const comboBoxFlatMap = allComboBoxOptions.flatMap(comboOption => {
      if (!updatedWhiteList.includes(comboOption.lookupFieldName)) return [];
      return [{ ...comboOption }];
    });

    formDefinitionFlatMap.map(currField => {
      const currentValue = updatedData[currField.FieldName];

      switch (currField.inputType) {
        case 'TextInput':
          if (currField.datatype === 'datetime' || currField.datatype === 'date') {
            if (!currentValue) break;
            // FormikField takes care of formatting and presentation
            setFieldValue(currField.FieldName, currentValue.split('T')[0]);
          } else if (currField.datatype === 'bit') {
            let bitValue;
            if (currentValue != null && currentValue !== '') {
              if (typeof currentValue === 'string') {
                bitValue = currentValue === 'true' ? 1 : 0;
              } else if (typeof currentValue === 'boolean') {
                bitValue = currentValue === true ? 1 : 0;
              } else if (typeof value === 'number') {
                bitValue = currentValue;
              }
            }
            setFieldValue(currField.FieldName, bitValue ?? '');
          } else {
            setFieldValue(currField.FieldName, currentValue);
          }
          break;

        case 'ComboBox':
          if (comboBoxFlatMap.length) {
            let currComboOptionDirty;
            comboBoxFlatMap.forEach(currOption => {
              if (currField.comboBoxLookupName === currOption.tableName) {
                currComboOptionDirty = currOption;
              }
            });

            const currentComboOption = currComboOptionDirty ? generateComboBoxOptions(currComboOptionDirty) : [];
            const multiSelectedOptions = [];
            let singleSelectOption = {};
            currentComboOption.forEach(opt => {
              if (currentValue) {
                if (currField.multiselect) {
                  currentValue.split(',').map(val => {
                    if (val === opt.value) {
                      multiSelectedOptions.push(opt);
                    }
                    return null;
                  });
                } else {
                  // eslint-disable-next-line no-lonely-if
                  if (currentValue === opt.value) {
                    singleSelectOption = opt;
                  }
                }
              }
            });

            if (currField.multiselect) {
              setFieldValue(currField.FieldName, multiSelectedOptions.map(option => option.value).join(','));
            } else {
              setFieldValue(currField.FieldName, singleSelectOption.value ?? '');
            }
          }
          break;

        default: break;
      }
      return null;
    });
  };

  // Goes through the office keys, and sets an object with the MemberOffice field name and AgentOffice field name
  const updatedOfficeKeys = (officeKeys, officeType) => (
    officeKeys.flatMap(key => {
      // eslint-disable-next-line no-unused-vars
      const [officePrefix, officeSuffix] = key.split('Office');
      const officeUpdatedKey = officeType.concat('Office', officeSuffix);
      let officeWhiteList = ['OfficeAOR', 'OfficeKeyNumeric', 'OfficeMlsId', 'OfficeName', 'OfficePhone'];

      if (officeType === 'Main') {
        officeWhiteList = ['OfficeKeyNumeric', 'OfficeMlsId', 'OfficeName'];
      }

      if (!officeWhiteList.includes(key)) return [];
      return [{
        base: key,
        updated: officeUpdatedKey,
      }];
    })
  );

  // Goes through the agent keys, and sets an object with the Member field name and Agent field name
  const updatedAgentKeys = (agentKeys, agentGroup) => (
    agentKeys.flatMap(key => {
      let agentWhiteList = ['MemberAOR', 'MemberEmail', 'MemberFullName', 'MemberMlsId', 'MemberDirectPhone'];
      if (agentGroup === 'OfficeManager') {
        agentWhiteList = ['MemberMlsId', 'MemberFullName', 'MemberKeyNumeric'];
      }
      if (!agentWhiteList.includes(key)) return [];

      let agentUpdatedKey;
      switch (key) {
        case 'MemberDirectPhone':
          if (agentGroup === 'ListAgent') {
            agentUpdatedKey = 'ListAgentDirectPhone';
            break;
          }
          if (agentGroup === 'CoListAgent') {
            agentUpdatedKey = 'CoListAgentMobilePhone';
            break;
          }
          if (agentGroup === 'BuyerAgent') {
            agentUpdatedKey = 'BuyerAgentDirectPhone';
            break;
          }
          if (agentGroup === 'CoBuyerAgent') {
            agentUpdatedKey = 'CoBuyerAgentMobilePhone';
            break;
          }
          break;

        default: {
          const [, agentSuffix] = key.split('Member');
          agentUpdatedKey = agentGroup.concat(agentSuffix);
          break;
        }
      }
      if (!agentUpdatedKey) return [];

      return [{
        base: key,
        updated: agentUpdatedKey,
      }];
    })
  );

  // Populating manager fields in create/edit office
  useEffect(() => {
    if (officeManagerLookup) {
      const memberKeys = Object.keys(officeManagerLookup);
      const memberGroup = 'OfficeManager';

      const filteredMemberKeys = updatedAgentKeys(memberKeys, memberGroup);
      filteredMemberKeys.map(key => {
        setFieldValue(key.updated, officeManagerLookup[key.base]);
        return null;
      });

      runValidation();
    }
  }, [officeManagerLookup]);

  // Populating agent lookup fields in create/edit listing
  useEffect(() => {
    if (agentLookup) {
      const memberKeys = Object.keys(agentLookup);
      const agentKeys = [];
      const officeKeys = [];

      let agentOfficeType = 'List';
      let agentGroup = 'ListAgent';

      if (agentGroupType) {
        [agentOfficeType] = agentGroupType.split('Agent');
        agentGroup = agentGroupType;
      }

      memberKeys.map(key => {
        if (key.includes('Member')) {
          agentKeys.push(key);
        } else if (key.includes('Office')) {
          officeKeys.push(key);
        }
        return null;
      });

      const filteredMemberKeys = updatedAgentKeys(agentKeys, agentGroup);
      const filteredOfficeKeys = updatedOfficeKeys(officeKeys, agentOfficeType);

      filteredMemberKeys.map(key => {
        setFieldValue(key.updated, agentLookup[key.base]);
        return null;
      });

      filteredOfficeKeys.map(key => {
        setFieldValue(key.updated, agentLookup[key.base]);
        return null;
      });

      const keyNumericKey = agentGroup.concat('KeyNumeric');
      setFieldValue(keyNumericKey, agentLookup.UniqueID);

      runValidation();
    }
  }, [agentLookup]);

  // This is for populating the office lookup fields when creating an agent
  useEffect(() => {
    if (agentOfficeLookup) {
      Object.entries(agentOfficeLookup).map(([key, value]) => {
        const officeWhiteList = ['OfficeKeyNumeric', 'OfficeID', 'OfficeMlsId', 'OfficeName']; // We know that these are the office fields are are looking for
        if (officeWhiteList.includes(key)) {
          setFieldValue(key, value);
        }
        return null;
      });
      runValidation();
    }
  }, [agentOfficeLookup]);

  // This is for populating the coordinates fields when using the location plotter
  useEffect(() => {
    if (currCoords) {
      Object.entries(currCoords).map(([key, value]) => {
        setFieldValue(key, value);
        return null;
      });
      runValidation();
    }
  }, [currCoords]);

  /*
    This useEffect listens to both the comboBox options and selectedListing
      because when editing a listing, we need to wait for the comboBox options
      to load before we update the fields, whereas with TAP we don't need to do that.
  */
  useEffect(() => {
    if (listingFormData && (!lookUpNames?.lookupNames?.length || allComboBoxOptions?.length)) {
      updateFormikValues(listingFormData);
      runValidation();
      dispatch(setFormDataInitialized());
    }
  }, [listingFormData, allComboBoxOptions]);

  // Populating fields when we edit an agent
  useEffect(() => {
    if (agentFormData && (!lookUpNames?.lookupNames?.length || allComboBoxOptions?.length)) {
      updateFormikValues(agentFormData);
      runValidation();
      dispatch(setFormDataInitialized());
    }
  }, [agentFormData, allComboBoxOptions]);

  // Populating the fields when editing an office
  useEffect(() => {
    if (officeFormData && (!lookUpNames?.lookupNames?.length || allComboBoxOptions?.length)) {
      updateFormikValues(officeFormData);
      runValidation();
      dispatch(setFormDataInitialized());
    }
  }, [officeFormData, allComboBoxOptions]);

  /*
    This is for when we are in the create/edit office form
      and do a Main Office Lookup
  */
  useEffect(() => {
    if (officeLookup) {
      const officeKeys = Object.keys(officeLookup);
      const filteredOfficeKeys = updatedOfficeKeys(officeKeys, 'Main');
      filteredOfficeKeys.map(key => {
        setFieldValue(key.updated, officeLookup[key.base]);
        return null;
      });
      runValidation();
    }
  }, [officeLookup]);

  return null;
};

export default FormikFieldUpdater;
